import React from "react";
import { Loader2Icon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AcademicianStartupButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant
   * @default "primary"
   */
  variant?: "primary" | "secondary" | "ghost" | "destructive";

  /**
   * Button size
   * @default "default"
   */
  size?: "sm" | "default" | "lg";

  /**
   * Loading state - shows spinner and disables button
   * @default false
   */
  loading?: boolean;

  /**
   * Icon to display on the left side
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display on the right side
   */
  rightIcon?: React.ReactNode;

  /**
   * Button content
   */
  children?: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const AcademicianStartupButton = React.forwardRef<
  HTMLButtonElement,
  AcademicianStartupButtonProps
>(
  (
    {
      variant = "primary",
      size = "default",
      loading = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Base styles
    const baseStyles =
      "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

    // Variant styles
    const variantStyles = {
      primary:
        "bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring active:bg-primary/80",
      secondary:
        "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-400 active:bg-gray-100 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-700 dark:active:bg-gray-600",
      ghost:
        "bg-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400 active:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 dark:active:bg-gray-700",
      destructive:
        "bg-[#EF4444] text-white hover:bg-[#dc2626] focus-visible:ring-[#EF4444] active:bg-[#b91c1c]",
    };

    // Size styles
    const sizeStyles = {
      sm: "h-8 px-3 text-sm",
      default: "h-10 px-4 text-base",
      lg: "h-12 px-6 text-lg",
    };

    // Icon size based on button size
    const iconSizeStyles = {
      sm: "h-4 w-4",
      default: "h-5 w-5",
      lg: "h-6 w-6",
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {loading && (
          <Loader2Icon className={cn("animate-spin", iconSizeStyles[size])} />
        )}
        {!loading && leftIcon && (
          <span className={cn("inline-flex", iconSizeStyles[size])}>
            {leftIcon}
          </span>
        )}
        {children}
        {!loading && rightIcon && (
          <span className={cn("inline-flex", iconSizeStyles[size])}>
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

AcademicianStartupButton.displayName = "AcademicianStartupButton";
